import React, { useEffect, useState } from "react";
import { saasApi } from "../../api/saasApi";
import { formatInrFromPaise } from "../../utils/validation";
import "../../styles/saas.css";

const EMPTY_FORM = {
  slug: "",
  name: "",
  description: "",
  monthly_price: "",
  yearly_price: "",
  trial_eligible: true,
  trial_days: 15,
  user_limit: "",
  storage_limit_mb: "",
  sort_order: 0,
};

export default function PlanManagement() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("info");
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const load = () => {
    setLoading(true);
    saasApi
      .adminPlans()
      .then((res) => setPlans(res.data || []))
      .catch((err) => {
        setMessageType("error");
        setMessage(err.message);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setModalOpen(true);
    setMessage("");
  };

  const openEdit = (plan) => {
    setEditingId(plan.id);
    setForm({
      slug: plan.slug,
      name: plan.name,
      description: plan.description || "",
      monthly_price: plan.monthly_price_paise ? plan.monthly_price_paise / 100 : "",
      yearly_price: plan.yearly_price_paise ? plan.yearly_price_paise / 100 : "",
      trial_eligible: Boolean(plan.trial_eligible),
      trial_days: plan.trial_days ?? 15,
      user_limit: plan.user_limit ?? "",
      storage_limit_mb: plan.storage_limit_mb ?? "",
      sort_order: plan.sort_order ?? 0,
    });
    setModalOpen(true);
    setMessage("");
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
  };

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const submitPlan = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage("");
    try {
      const payload = {
        slug: form.slug.trim().toLowerCase().replace(/\s+/g, "-"),
        name: form.name.trim(),
        description: form.description.trim() || null,
        monthly_price: Number(form.monthly_price) || 0,
        yearly_price: Number(form.yearly_price) || 0,
        trial_eligible: form.trial_eligible,
        trial_days: Number(form.trial_days) || 15,
        user_limit: form.user_limit === "" ? null : Number(form.user_limit),
        storage_limit_mb: form.storage_limit_mb === "" ? null : Number(form.storage_limit_mb),
        sort_order: Number(form.sort_order) || 0,
      };

      if (editingId) {
        await saasApi.updatePlan(editingId, payload);
        setMessageType("success");
        setMessage("Plan updated.");
      } else {
        await saasApi.createPlan(payload);
        setMessageType("success");
        setMessage("Plan created.");
      }
      closeModal();
      load();
    } catch (err) {
      setMessageType("error");
      setMessage(err.data?.message || err.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const deactivate = async (id) => {
    if (!window.confirm("Deactivate this plan? It will no longer appear on the public pricing page.")) return;
    await saasApi.deactivatePlan(id);
    load();
  };

  const reactivate = async (plan) => {
    await saasApi.updatePlan(plan.id, { is_active: true });
    load();
  };

  return (
    <div className="saas-page" style={{ minHeight: "auto", background: "transparent" }}>
      <div className="saas-card">
        <h1>Platform subscription plans</h1>
       
        <p className="saas-hint" style={{ marginBottom: "1rem" }}>
          <strong>Starter / Growth / Enterprise</strong> are default seed plans from the database
          migration so self-service signup works on day one. You can edit prices, add new plans, or
          deactivate any of them.
        </p>
        <button type="button" className="saas-btn saas-btn--primary" onClick={openCreate}>
          + Create platform plan
        </button>
      </div>

      {message && !modalOpen && (
        <p className={messageType === "error" ? "saas-error" : "saas-success"}>{message}</p>
      )}

      {loading ? (
        <p>Loading plans…</p>
      ) : (
        <table className="saas-compare-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Slug</th>
              <th>Monthly</th>
              <th>Yearly</th>
              <th>Trial</th>
              <th>Users</th>
              <th>Active</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {plans.map((p) => (
              <tr key={p.id}>
                <td>{p.name}</td>
                <td><code>{p.slug}</code></td>
                <td>{formatInrFromPaise(p.monthly_price_paise)}</td>
                <td>{formatInrFromPaise(p.yearly_price_paise)}</td>
                <td>{p.trial_eligible ? `${p.trial_days}d` : "No"}</td>
                <td>{p.user_limit ?? "∞"}</td>
                <td>{p.is_active ? "Yes" : "No"}</td>
                <td style={{ whiteSpace: "nowrap" }}>
                  <button type="button" className="saas-btn saas-btn--secondary" onClick={() => openEdit(p)}>
                    Edit
                  </button>{" "}
                  {p.is_active ? (
                    <button type="button" className="saas-btn saas-btn--secondary" onClick={() => deactivate(p.id)}>
                      Deactivate
                    </button>
                  ) : (
                    <button type="button" className="saas-btn saas-btn--secondary" onClick={() => reactivate(p)}>
                      Activate
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {modalOpen && (
        <div className="saas-modal-backdrop" onClick={closeModal}>
          <div className="saas-modal" onClick={(e) => e.stopPropagation()}>
            <h2>{editingId ? "Edit platform plan" : "Create platform plan"}</h2>
            {message && modalOpen && (
              <p className={messageType === "error" ? "saas-error" : "saas-success"}>{message}</p>
            )}
            <form onSubmit={submitPlan} className="saas-form">
              <label>
                Slug (URL id) *
                <input
                  name="slug"
                  value={form.slug}
                  onChange={onChange}
                  disabled={Boolean(editingId)}
                  placeholder="e.g. pro, business"
                  required
                />
              </label>
              <label>
                Display name *
                <input name="name" value={form.name} onChange={onChange} required />
              </label>
              <label>
                Description
                <textarea name="description" value={form.description} onChange={onChange} />
              </label>
              <label>
                Monthly price (INR)
                <input name="monthly_price" type="number" min="0" value={form.monthly_price} onChange={onChange} />
              </label>
              <label>
                Yearly price (INR)
                <input name="yearly_price" type="number" min="0" value={form.yearly_price} onChange={onChange} />
              </label>
              <label>
                <input name="trial_eligible" type="checkbox" checked={form.trial_eligible} onChange={onChange} />
                {" "}Offer free trial
              </label>
              <label>
                Trial days
                <input name="trial_days" type="number" min="0" value={form.trial_days} onChange={onChange} />
              </label>
              <label>
                User limit (blank = unlimited)
                <input name="user_limit" type="number" min="1" value={form.user_limit} onChange={onChange} />
              </label>
              <label>
                Storage limit (MB)
                <input name="storage_limit_mb" type="number" min="1" value={form.storage_limit_mb} onChange={onChange} />
              </label>
              <label>
                Sort order
                <input name="sort_order" type="number" value={form.sort_order} onChange={onChange} />
              </label>
              <div style={{ display: "flex", gap: "0.5rem" }}>
                <button type="submit" className="saas-btn saas-btn--primary" disabled={saving}>
                  {saving ? "Saving…" : editingId ? "Update plan" : "Create plan"}
                </button>
                <button type="button" className="saas-btn saas-btn--secondary" onClick={closeModal}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
